import * as React from 'react';
import { Box, Typography, Skeleton, Paper, IconButton, Alert, Tooltip, Button } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useRecoilState } from 'recoil';
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import backendApi from '../services/axios.auth.wrapper';
import { StaticticsStickyNavbar } from '../components/statistics/StatisticsStickyNavbar';

import { GetCameraData, GetSiteData, GetSpeciesList } from '../hooks/statistics.hooks';
import { useInitializeSiteAndCamera } from '../hooks/useInitializeSiteAndCamera';

import {
  dateRangeState,
  dateValueState,
  siteState,
  cameraState,
  selectedOrdersState,
  selectedSpeciesState,
  dateRangeAdjustedState,
  speciesListState,
  speciesFilterModeState,
} from '../state/statistics.state';

const baseURL = process.env.REACT_APP_BACKEND_URL;

interface TurbineInteractionCompositeRow {
  id: number;
  camera_1_id: number;
  camera_2_id: number;
  start_time: string;
  duration: number;
  bucket_name: string;
  object_name: string;
  interaction?: string;
  reprojection_error?: number;
}

interface TurbineInteractionResponse {
  results: TurbineInteractionCompositeRow[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

interface SignedUrlRequest {
  bucket_name: string;
  object_name: string;
}

export default function TurbineInteraction(): JSX.Element {
  const pageSize = 50;

  const [site, setSite] = useRecoilState(siteState);
  const [camera, setCamera] = useRecoilState(cameraState);
  const [dateRange, setDateRange] = useRecoilState(dateRangeState);
  const [selectedOrders, setSelectedOrders] = useRecoilState(selectedOrdersState);
  const [selectedSpecies, setSelectedSpecies] = useRecoilState(selectedSpeciesState);
  const [dateValue, setDateValue] = useRecoilState(dateValueState);
  const [, setHasAdjustedDateRange] = useRecoilState(dateRangeAdjustedState);
  const [speciesFilterMode, setSpeciesFilterMode] = useRecoilState(speciesFilterModeState);

  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [species, setSpecies] = useRecoilState(speciesListState);
  const queryClient = useQueryClient();

  const { data: siteData } = GetSiteData();
  const { data: cameraData } = GetCameraData(site);
  GetSpeciesList(setSpecies, dateRange, site, camera);
  const shouldFilterSpecies =
    speciesFilterMode === 'custom' && (selectedOrders.length > 0 || selectedSpecies.length > 0);
  const filteredOrders = React.useMemo(
    () => (shouldFilterSpecies ? selectedOrders : []),
    [shouldFilterSpecies, selectedOrders],
  );
  const filteredSpecies = React.useMemo(
    () => (shouldFilterSpecies ? selectedSpecies : []),
    [shouldFilterSpecies, selectedSpecies],
  );

  useInitializeSiteAndCamera();

  const startDateKey = dateRange?.startDate ? dateRange.startDate.getTime() : null;
  const endDateKey = dateRange?.endDate ? dateRange.endDate.getTime() : null;
  const cameraKey = Array.isArray(camera) ? camera.join(',') : '';
  const speciesOrdersKey = filteredOrders.join('|');
  const speciesNamesKey = filteredSpecies.join('|');
  const normalizedCameraIds = React.useMemo(
    () => (Array.isArray(camera) ? camera.map((id) => Number(id)) : []),
    [camera],
  );

  React.useEffect(() => {
    setCurrentPage(1);
  }, [site, cameraKey, startDateKey, endDateKey, speciesOrdersKey, speciesNamesKey]);

  const cameraCount = Array.isArray(camera) ? camera.length : 0;
  const hasTwoOrMoreCamerasSelected = cameraCount >= 2;
  const hasSiteAndDates =
    Boolean(site) &&
    Boolean(dateRange?.startDate) &&
    Boolean(dateRange?.endDate);
  const hasRequiredFilters =
    hasSiteAndDates && hasTwoOrMoreCamerasSelected;

  const fetchPageData = React.useCallback(
    async (pageNumber: number): Promise<TurbineInteractionResponse | null> => {
      if (!hasRequiredFilters || !site || normalizedCameraIds.length === 0 || !dateRange) {
        return null;
      }

      const formatDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const response = await backendApi.post(`${baseURL}/api/hammerhead_segments`, {
        site_id: site,
        camera_ids: normalizedCameraIds,
        start_date: formatDate(dateRange.startDate),
        end_date: formatDate(dateRange.endDate),
        limit_per_camera: pageSize,
        page: pageNumber,
        species_order: filteredOrders,
        species_name: filteredSpecies,
      });

      return response.data as TurbineInteractionResponse;
    },
    [dateRange, filteredOrders, filteredSpecies, hasRequiredFilters, normalizedCameraIds, pageSize, site],
  );

  const queryKey = [
    'turbine-interaction-segments',
    site,
    cameraKey,
    startDateKey,
    endDateKey,
    currentPage,
    pageSize,
    speciesOrdersKey,
    speciesNamesKey,
  ];

  const {
    data: compositeResponse,
    isLoading,
    isFetching,
    error: compositeError,
  } = useQuery(queryKey, () => fetchPageData(currentPage), {
    enabled: hasRequiredFilters,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
  });

  React.useEffect(() => {
    const totalPagesFromResponse = compositeResponse?.total_pages ?? 0;
    if (totalPagesFromResponse > 0 && currentPage > totalPagesFromResponse) {
      setCurrentPage(totalPagesFromResponse);
    } else if (totalPagesFromResponse === 0 && currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [compositeResponse?.total_pages, currentPage]);

  React.useEffect(() => {
    if (!compositeResponse || !hasRequiredFilters) {
      return;
    }

    const totalPagesFromResponse = compositeResponse.total_pages ?? 0;
    const pagesToPrefetch: number[] = [];

    if (currentPage > 1) {
      pagesToPrefetch.push(currentPage - 1);
    }
    if (currentPage < totalPagesFromResponse) {
      pagesToPrefetch.push(currentPage + 1);
    }

    pagesToPrefetch.forEach((pageNumber) => {
      queryClient.prefetchQuery(
        [
          'turbine-interaction-segments',
          site,
          cameraKey,
          startDateKey,
          endDateKey,
          pageNumber,
          pageSize,
        ],
        () => fetchPageData(pageNumber),
        { staleTime: 5 * 60 * 1000 },
      );
    });
  }, [
    compositeResponse,
    currentPage,
    fetchPageData,
    hasRequiredFilters,
    queryClient,
    site,
    cameraKey,
    startDateKey,
    endDateKey,
    pageSize,
  ]);

  const items: TurbineInteractionCompositeRow[] = React.useMemo(() => {
    if (!compositeResponse?.results || !Array.isArray(compositeResponse.results)) {
      return [];
    }
    return compositeResponse.results.filter(
      (row) => row.reprojection_error != null && row.reprojection_error < 20,
    );
  }, [compositeResponse]);

  const sortedItems = React.useMemo(() => {
    const list = [...items];
    list.sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime());
    return list;
  }, [items]);

  React.useEffect(() => {
    setSelectedIndex(0);
  }, [currentPage, sortedItems.length]);

  const selectedItem = sortedItems[selectedIndex] ?? null;

  const deriveAlternateObjectNames = React.useCallback((objectName: string): string[] => {
    const match = objectName.match(/^(.*)\.(mp4|webm)$/i);
    if (!match) {
      return [];
    }
    const [, base, ext] = match;
    if (ext.toLowerCase() === 'mp4') {
      return [`${base}.webm`];
    }
    if (ext.toLowerCase() === 'webm') {
      return [`${base}.mp4`];
    }
    return [];
  }, []);

  const signedRequests = React.useMemo(() => {
    const list: SignedUrlRequest[] = [];
    const seen = new Set<string>();

    const enqueue = (bucket: string, objectName?: string | null) => {
      if (!objectName) {
        return;
      }
      const key = `${bucket}|${objectName}`;
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      list.push({ bucket_name: bucket, object_name: objectName });
    };

    sortedItems.forEach((row) => {
      enqueue(row.bucket_name, row.object_name);
      deriveAlternateObjectNames(row.object_name).forEach((alt) => enqueue(row.bucket_name, alt));
    });

    return list;
  }, [sortedItems, deriveAlternateObjectNames]);

  const signedResults = useQueries({
    queries: signedRequests.map((req) => ({
      queryKey: ['signed-url', req.bucket_name, req.object_name],
      queryFn: async () => (await backendApi.post(`${baseURL}/api/signedurl`, req)).data as string,
      enabled: Boolean(req.bucket_name && req.object_name),
      staleTime: 60000,
    })),
  });

  const objectNameToUrl = React.useMemo(() => {
    const map = new Map<string, string>();
    signedResults.forEach((queryResult, index) => {
      const request = signedRequests[index];
      if (queryResult.data && request) {
        map.set(request.object_name, queryResult.data as string);
      }
    });
    return map;
  }, [signedResults, signedRequests]);

  const cameraIdToName = React.useMemo(() => {
    const map = new Map<number, string>();
    const cameras = cameraData?.data as Array<{ id: number; camera_name: string }> | undefined;
    if (cameras) {
      cameras.forEach((cameraMeta) => map.set(cameraMeta.id, cameraMeta.camera_name));
    }
    return map;
  }, [cameraData]);

  const getVideoSources = React.useCallback(
    (row: TurbineInteractionCompositeRow | null | undefined): Array<{ url: string; type: string }> => {
      if (!row) {
        return [];
      }

      const objectName = row.object_name;
      const lower = objectName.toLowerCase();
      const candidates: string[] = [];

      if (lower.endsWith('.mp4')) {
        const base = objectName.slice(0, -4);
        candidates.push(`${base}.webm`, objectName);
      } else if (lower.endsWith('.webm')) {
        const base = objectName.slice(0, -5);
        candidates.push(objectName, `${base}.mp4`);
      } else {
        candidates.push(objectName);
      }

      const unique = new Set<string>();
      const sources: Array<{ url: string; type: string }> = [];

      candidates.forEach((candidate) => {
        if (unique.has(candidate)) {
          return;
        }
        const url = objectNameToUrl.get(candidate);
        if (!url) {
          return;
        }

        unique.add(candidate);
        const lowerCandidate = candidate.toLowerCase();
        const type = lowerCandidate.endsWith('.webm')
          ? 'video/webm'
          : lowerCandidate.endsWith('.mp4')
            ? 'video/mp4'
            : 'video/mp4';
        sources.push({ url, type });
      });

      if (sources.length === 0) {
        const fallback = objectNameToUrl.get(objectName);
        if (fallback) {
          const type = lower.endsWith('.webm') ? 'video/webm' : lower.endsWith('.mp4') ? 'video/mp4' : 'video/mp4';
          sources.push({ url: fallback, type });
        }
      }

      return sources;
    },
    [objectNameToUrl]
  );

  const getShortCamLabel = (cameraId: number): string => {
    const name = cameraIdToName.get(cameraId) ?? `Cam ${cameraId}`;
    const match = name.match(/K\d+/i);
    return match ? match[0].toUpperCase() : name;
  };

  const formatDuration = React.useCallback((durationInSeconds?: number | null): string => {
    if (durationInSeconds == null || Number.isNaN(durationInSeconds) || durationInSeconds <= 0) {
      return '—';
    }
    if (durationInSeconds < 60) {
      return `${durationInSeconds.toFixed(1)}s`;
    }
    if (durationInSeconds < 3600) {
      const minutes = Math.floor(durationInSeconds / 60);
      const seconds = Math.floor(durationInSeconds % 60);
      return `${minutes}m ${seconds}s`;
    }
    const hours = Math.floor(durationInSeconds / 3600);
    const minutes = Math.floor((durationInSeconds % 3600) / 60);
    const seconds = Math.floor(durationInSeconds % 60);
    return `${hours}h ${minutes}m ${seconds}s`;
  }, []);

  const formatTimestamp = React.useCallback((timestamp?: string): string => {
    if (!timestamp) {
      return '—';
    }
    return new Date(timestamp).toLocaleString();
  }, []);

  const formatListTimestamp = React.useCallback((timestamp: string): string => {
    const date = new Date(timestamp);
    const datePart = date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
    const timePart = date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${datePart} · ${timePart}`;
  }, []);

  const showMissingFiltersBanner =
    !hasSiteAndDates || !hasTwoOrMoreCamerasSelected;
  const hasResults = sortedItems.length > 0;
  const totalResults = compositeResponse?.total ?? 0;
  const computedTotalPages = compositeResponse?.total_pages ?? (totalResults > 0 ? Math.ceil(totalResults / pageSize) : 0);
  const totalPages = Math.max(1, computedTotalPages > 0 ? computedTotalPages : 1);
  const rangeStart = totalResults === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = totalResults === 0 ? 0 : rangeStart + sortedItems.length - 1;
  const isInitialLoading = isLoading && !compositeResponse;
  const shouldShowPagination = totalResults > pageSize && totalPages > 1;

  return (
    <Box sx={{ backgroundColor: '#F8F9FA', flexGrow: 1, minHeight: '100vh', paddingTop: '50px' }}>
      <>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          <Box sx={{ width: '25%' }}></Box>
          <Box sx={{ display: 'grid', rowGap: '8px', width: '1000px' }}>
            <h1 style={{ fontSize: '32px', lineHeight: '40px', margin: 0, fontWeight: 600, color: '#17052D' }}>
              Turbine Interaction
            </h1>
            <p style={{ fontSize: '16px', lineHeight: '24px', color: '#6B7280', margin: '8px 0 0 0' }}>
              Review composite videos of turbine interaction events captured across paired cameras
            </p>
          </Box>
        </Box>

        <StaticticsStickyNavbar
          dateRange={dateRange}
          setDateRange={setDateRange}
          site={site}
          setSite={setSite}
          listOfSites={siteData}
          updateCameraDetails={(_event: unknown, newCameraIds: number[] | string[]) => {
            const normalized = (newCameraIds as Array<number | string>).map((id) => Number(id));
            setCamera(normalized);
          }}
          cameraData={cameraData}
          camera={camera}
          speciesList={species}
          setSelectedOrders={setSelectedOrders}
          setSelectedSpecies={setSelectedSpecies}
          selectedOrders={selectedOrders}
          selectedSpecies={selectedSpecies}
          dateValue={dateValue}
          setDateValue={setDateValue}
          showDownloadButtons={false}
        setDateRangeAdjusted={setHasAdjustedDateRange}
        setSpeciesFilterMode={setSpeciesFilterMode}
        />

        {showMissingFiltersBanner && (
          <Box sx={{ display: 'flex', width: '100%', mt: 4 }}>
            <Box sx={{ width: '25%' }} />
            <Box
              sx={{
                width: '1000px',
                maxWidth: 'calc(100vw - 25%)',
                px: 3,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Alert severity="warning" sx={{ maxWidth: 700, width: '100%', textAlign: 'center' }}>
                Select a site, two or more cameras, and a start and end date to load turbine interaction events.
              </Alert>
            </Box>
          </Box>
        )}

        {compositeError && !showMissingFiltersBanner && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Alert severity="error" sx={{ maxWidth: 600 }}>
              Failed to load interaction data. Please try refreshing the page.
            </Alert>
          </Box>
        )}

        <Box sx={{ display: 'flex', width: '100%' }}>
          <Box sx={{ width: '25%' }}></Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '1000px',
              maxWidth: 'calc(100vw - 25%)',
              paddingTop: '24px',
              paddingBottom: '48px',
              gap: '32px',
              overflow: 'visible',
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: '16px',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(229, 231, 235, 0.8)',
                background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ paddingBottom: 2, borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '18px' }}>
                    Turbine interaction results
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6B7280' }}>
                    {isInitialLoading ? 'Loading...' : `${totalResults} results${totalResults > 0 ? ` · Showing ${rangeStart}-${rangeEnd}` : ''}`}
                  </Typography>
                </Box>
                {isFetching && compositeResponse ? (
                  <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                    Updating…
                  </Typography>
                ) : null}
              </Box>
              <Box
                sx={{
                  maxHeight: 400,
                  overflowY: 'auto',
                  mt: 2,
                  pr: 1,
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(120, 0, 240, 0.3)',
                    borderRadius: '999px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(17, 24, 39, 0.05)',
                    borderRadius: '999px',
                  },
                }}
              >
                {isInitialLoading && (
                  <Box sx={{ padding: 2 }}>
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Box key={index} sx={{ padding: '8px 12px' }}>
                        <Skeleton variant="text" width={150} height={18} />
                        <Skeleton variant="text" width={160} height={14} />
                      </Box>
                    ))}
                  </Box>
                )}
                {totalResults === 0 && !isInitialLoading && !showMissingFiltersBanner && (
                  <Box sx={{ padding: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No turbine interaction results found for the selected criteria.
                    </Typography>
                  </Box>
                )}
                {sortedItems.map((row, idx) => {
                  const isSelected = idx === selectedIndex;
                  return (
                    <Box
                      key={`${row.id}-${idx}`}
                      onClick={() => setSelectedIndex(idx)}
                      sx={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        backgroundColor: isSelected ? 'rgba(120,0,240,0.08)' : 'transparent',
                        borderRadius: '8px',
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: isSelected ? 'rgba(120,0,240,0.12)' : 'rgba(120,0,240,0.04)',
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: '13px', fontWeight: isSelected ? 600 : 500 }}>
                        {formatListTimestamp(row.start_time)}
                      </Typography>
                      <Box
                        sx={{ display: 'flex', justifyContent: 'space-between', color: '#6B7280', fontSize: '12px', mt: 0.5 }}
                      >
                        <span>{`${getShortCamLabel(row.camera_1_id)} + ${getShortCamLabel(row.camera_2_id)}`}</span>
                        <span>{formatDuration(row.duration)}</span>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {shouldShowPagination && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 3, gap: 1 }}>
                  <Typography variant="caption" sx={{ color: '#6B7280' }}>
                    Page {currentPage} of {totalPages}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      if (currentPage > 1) {
                        setSelectedIndex(0);
                        setCurrentPage((prev) => Math.max(1, prev - 1));
                      }
                    }}
                    disabled={currentPage === 1 || (isFetching && !isInitialLoading)}
                    sx={{ minWidth: 32, px: 1 }}
                  >
                    {'<'}
                  </Button>
                  <Typography variant="body2" sx={{ minWidth: 28, textAlign: 'center', fontWeight: 600 }}>
                    {currentPage}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      if (currentPage < totalPages) {
                        setSelectedIndex(0);
                        setCurrentPage((prev) => Math.min(totalPages, prev + 1));
                      }
                    }}
                    disabled={currentPage === totalPages || (isFetching && !isInitialLoading)}
                    sx={{ minWidth: 32, px: 1 }}
                  >
                    {'>'}
                  </Button>
                </Box>
              )}
            </Paper>

            {selectedItem || (!hasResults && !isInitialLoading && !showMissingFiltersBanner) ? (
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: '16px',
                  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
                  border: '1px solid rgba(229, 231, 235, 0.8)',
                  background: 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="overline" sx={{ color: '#6B7280' }}>
                      Timestamp
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {selectedItem ? formatTimestamp(selectedItem.start_time) : '—'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Previous result (←)">
                      <span>
                        <IconButton
                          size="small"
                          disabled={selectedIndex <= 0}
                          onClick={() => setSelectedIndex((prev) => Math.max(0, prev - 1))}
                        >
                          <ChevronLeftIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Next result (→)">
                      <span>
                        <IconButton
                          size="small"
                          disabled={selectedIndex >= sortedItems.length - 1}
                          onClick={() => setSelectedIndex((prev) => Math.min(sortedItems.length - 1, prev + 1))}
                        >
                          <ChevronRightIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  {(() => {
                    if (!selectedItem) {
                      return (
                        <Skeleton variant="rectangular" width="100%" height={500} sx={{ borderRadius: '8px' }} />
                      );
                    }
                    const sources = getVideoSources(selectedItem);
                    const primarySource = sources[0];

                    return (
                      <Box sx={{ width: '100%', maxWidth: '900px' }}>
                        {primarySource ? (
                          <video
                            key={`${selectedItem.id}-${primarySource.url}`}
                            controls
                            autoPlay
                            loop
                            playsInline
                            style={{
                              width: '100%',
                              height: 'auto',
                              borderRadius: '8px',
                              backgroundColor: '#000',
                            }}
                          >
                            {sources.map((source) => (
                              <source
                                key={`${selectedItem?.id ?? 'source'}-${source.type}`}
                                src={source.url}
                                type={source.type}
                              />
                            ))}
                            Your browser does not support HTML5 video.
                          </video>
                        ) : (
                          <Skeleton variant="rectangular" width="100%" height={500} sx={{ borderRadius: '8px' }} />
                        )}
                      </Box>
                    );
                  })()}
                </Box>

                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-start' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 1, maxWidth: '420px' }}>
                    <Typography sx={{ color: '#6B7280' }}>Camera pair</Typography>
                    <Typography>
                      {selectedItem
                        ? `${getShortCamLabel(selectedItem.camera_1_id)} + ${getShortCamLabel(selectedItem.camera_2_id)}`
                        : '—'}
                    </Typography>
                    <Typography sx={{ color: '#6B7280' }}>Camera 1</Typography>
                    <Typography>
                      {selectedItem ? cameraIdToName.get(selectedItem.camera_1_id) ?? `Cam ${selectedItem.camera_1_id}` : '—'}
                    </Typography>
                    <Typography sx={{ color: '#6B7280' }}>Camera 2</Typography>
                    <Typography>
                      {selectedItem ? cameraIdToName.get(selectedItem.camera_2_id) ?? `Cam ${selectedItem.camera_2_id}` : '—'}
                    </Typography>
                    <Typography sx={{ color: '#6B7280' }}>Duration</Typography>
                    <Typography>{selectedItem ? formatDuration(selectedItem.duration) : '—'}</Typography>
                    {selectedItem?.interaction ? (
                      <>
                        <Typography sx={{ color: '#6B7280' }}>Interaction</Typography>
                        <Typography>{selectedItem.interaction}</Typography>
                      </>
                    ) : null}
                  </Box>
                </Box>
              </Paper>
            ) : null}
          </Box>
        </Box>
      </>
    </Box>
  );
}


